<template>
    <slot name="stone-type"></slot>

    <FilterAccordion
        v-qa="'filter-price'"
        :count="
            modelValue?.priceRange?.min?.amount &&
            modelValue?.priceRange?.max?.amount
                ? 1
                : 0
        "
        :title="t('diamond_search.filter.price.input')"
        hide-count
    >
        <slot name="price"></slot>
    </FilterAccordion>

    <FilterAccordion
        :count="
            modelValue?.caratWeightRange?.min &&
            modelValue?.caratWeightRange?.max
                ? 1
                : 0
        "
        :title="t('diamond_search.filter.carat_size.input')"
        hide-count
    >
        <FilterRange
            v-model="caratWeightModel"
            v-qa="'filter-carat-size'"
            :bounds="bounds.caratWeight"
            :step="0.05"
            :value-formatter="(value: number) => value.toFixed(2)"
            :min-label="'Minimum carat size'"
            :max-label="'Maximum carat size'"
        />
    </FilterAccordion>

    <FilterAccordion
        v-if="bounds.colors.length"
        :count="colorModel.length"
        :title="t('diamond_search.filter.colour.input')"
    >
        <FilterToggle
            v-model="colorModel"
            v-qa="'filter-colour'"
            :bounds="bounds.colors.map((c) => c.systemCode)"
            :formatter="(value: string) => value.replace(`DIAMOND_`, '')"
        />
    </FilterAccordion>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type {
    NonSkuCaratWeightRangeInput,
    NonSkuLabGrownDiamondBoundsFragment,
    NonSkuLabGrownDiamondsFilters,
    StoneColorCode,
} from '@/graphql/api'

import FilterRange from '@/components/stone/search/nonsku/filter/FilterRange.vue'
import FilterToggle from '@/components/stone/search/sku/filter/FilterToggle.vue'
import FilterAccordion from '@/components/ui/filter/FilterAccordion.vue'

const props = defineProps<{
    modelValue: NonSkuLabGrownDiamondsFilters | null | undefined
    bounds: NonSkuLabGrownDiamondBoundsFragment
}>()

const emit = defineEmits<{
    'update:modelValue': [v: NonSkuLabGrownDiamondsFilters]
}>()

const { t } = useI18n()

const caratWeightModel = computed({
    get(): NonSkuCaratWeightRangeInput | null | undefined {
        const values = props.modelValue

        if (!values) {
            return props.bounds.caratWeight
        }

        if (!values.caratWeightRange) {
            return props.bounds.caratWeight
        }

        return values.caratWeightRange
    },
    set(range: NonSkuCaratWeightRangeInput | null | undefined) {
        emit('update:modelValue', {
            ...props.modelValue,
            caratWeightRange: range,
        })
    },
})

const colorModel = computed({
    get(): StoneColorCode[] {
        return props.modelValue?.colors || []
    },
    set(colors: StoneColorCode[]) {
        emit('update:modelValue', {
            ...props.modelValue,
            colors: colors,
        })
    },
})
</script>
